
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  useLocation,
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";

import "./Sidebar.css";
import HostelCard from "../HostelCard/HostelCard";
import FoodSupplierCard from "../HostelCard/FoodSupplierCard";
import Header from "../Header/Header";
import axiosApi from "../Utils/axiosApi";
import FallbackImage from "../../assets/hotel-1.jpeg";
import { Helmet } from "react-helmet-async";
import Pagination from "@mui/material/Pagination";
import AIChatWidget from "../AIAgent/AIChatWidget";
import { SITE_CONFIG } from "../../config/siteConfig";

import {
  Skeleton,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";

import LoginPage from "../Login/LoginPage";
import Contact from "../Utils/Contact";

const foodSuppliers = [
  {
    image: FallbackImage,
    name: "Supplier 1",
    location: "Location 1",
  },
  {
    image: FallbackImage,
    name: "Supplier 2",
    location: "Location 2",
  },
];

const getHostelImage = (images) => {
  const validImages = Array.isArray(images) ? images : [];

  const profile = validImages.find(
    (img) => img.imageCategory === "PROFILE"
  );

  const interior = validImages.find(
    (img) => img.imageCategory === "INTERIOR"
  );

  return (
    profile?.imageUrl ||
    interior?.imageUrl ||
    FallbackImage
  );
};

const formatSlugToName = (slug = "") =>
  decodeURIComponent(slug)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { hostelSlug, citySlug } = useParams();

  const params = new URLSearchParams(location.search);

  const searchDates = {
    checkIn: params.get("searchCheckInDate"),
    checkOut: params.get("searchCheckOutDate"),
  };

  // --------------------------------------------------
  // SITE CONFIGURATION
  // --------------------------------------------------

  const {
    key: siteKey,
    brandName = "HostelandPG",
    propertyTypes = ["HOSTEL", "PG"],
    propertyLabel = "Hostels & PGs",
   
    listingPath = "/hostel",
    defaultTitle = "Best Hostels & PGs Near You | HostelandPG",
    defaultDescription =
      "Find verified hostels and PG accommodation.",
    canonicalBase = "https://www.hostelandpg.com",
    logo = "/logo1.png",
    heroTitle,
    heroDescription,
    noResultsTitle,
    noResultsDescription,
    cityTitle,
  } = SITE_CONFIG;


  // Local testing: preserve ?site=guestopsy during navigation.
  // Production: the domain determines the active brand.
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const getSitePath = useCallback(
    (path) => {
      return isLocal
        ? `${path}?site=${siteKey}`
        : path;
    },
    [isLocal, siteKey]
  );

  const getCityPath = useCallback(
    (city) => getSitePath(`/city/${city}`),
    [getSitePath]
  );

  // --------------------------------------------------
  // STATE
  // --------------------------------------------------

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [loginOpen, setLoginOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [size] = useState(15);
  const [totalPages, setTotalPages] = useState(0);

  const [filters, setFilters] = useState({
    sortOption: "",
    preference: "",
    gender: "",
    occupancy: [],
    priceRange: [0, 10000],
  });

  // --------------------------------------------------
  // ROUTE CHECKS
  // --------------------------------------------------

  const isCityPage = useMemo(
    () => location.pathname.startsWith("/city/"),
    [location.pathname]
  );

  const cityNameFromSlug = useMemo(() => {
    if (!isCityPage || !citySlug) return null;

    return formatSlugToName(citySlug);
  }, [isCityPage, citySlug]);

  const showHostels = useMemo(
    () =>
      location.pathname === "/" ||
      location.pathname.startsWith("/hostel") ||
      location.pathname.startsWith("/property") ||
      location.pathname.startsWith("/city"),
    [location.pathname]
  );

  const showFoodSuppliers = useMemo(
    () => location.pathname === "/foodSupplier",
    [location.pathname]
  );

  // --------------------------------------------------
  // AI SEARCH FILTERS
  // --------------------------------------------------

  const aiParams = new URLSearchParams(location.search);

  const aiFilters = [
    aiParams.get("searchText"),
    aiParams.get("gender"),
    aiParams.get("preference"),
    aiParams.get("maxPrice") &&
      `Under ₹${aiParams.get("maxPrice")}`,
    aiParams.get("wifi") === "true" && "WiFi",
    aiParams.get("food") === "true" && "Food",
    aiParams.get("gym") === "true" && "Gym",
    aiParams.get("parking") === "true" && "Parking",
    aiParams.get("laundry") === "true" && "Laundry",
    aiParams.get("searchCheckInDate"),
  ].filter(Boolean);

  const isAISearch = aiParams.get("ai") === "true";

  // --------------------------------------------------
  // SCROLL TO TOP
  // --------------------------------------------------

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // --------------------------------------------------
  // FETCH LISTINGS
  // --------------------------------------------------

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    setError(null);

    console.log("Hostname:", window.location.hostname);
    console.log("Site key:", siteKey);
    console.log("Property types:", propertyTypes);
    console.log("Current URL:", window.location.href);

    try {
      const params = new URLSearchParams(location.search);

      const gender =
        params.get("gender") || filters.gender;

      // Active domain controls property types.
      const activePropertyTypes = propertyTypes;

      const preference =
        params.get("preference") || filters.preference;

      const sort =
        params.get("sort") || filters.sortOption;

      const occupancyParam = params.get("occupancy");

      const occupancy = occupancyParam
        ? occupancyParam.split(",")
        : filters.occupancy;

      const minPrice = params.get("minPrice")
        ? Number(params.get("minPrice"))
        : filters.priceRange[0];

      const maxPrice = params.get("maxPrice")
        ? Number(params.get("maxPrice"))
        : filters.priceRange[1];

      const food = params.get("food") === "true";
      const wifi = params.get("wifi") === "true";
      const gym = params.get("gym") === "true";
      const parking = params.get("parking") === "true";
      const laundry = params.get("laundry") === "true";

      const searchText =
        params.get("searchText")?.trim() || "";

      const cityIdsParam = params.get("cityIds");

      const cityIds = cityIdsParam
        ? cityIdsParam
            .split(",")
            .map(Number)
            .filter((id) => !isNaN(id))
        : [];

      const checkIn = params.get("searchCheckInDate");
      const checkOut = params.get("searchCheckOutDate");

      // Base API payload
      const payload = {
        pageNo: page + 1,
        pageSize: size,

        hostelSearchCriteria: {
          searchText: "",
          cityIds: [],
          preference,
          gender,
          occupancy,
          priceMin: minPrice,
          priceMax: maxPrice,
          sortBy: sort,

          food,
          wifi,
          gym,
          parking,
          laundry,

          searchCheckInDate: checkIn || null,
          searchCheckOutDate: checkOut || null,

          propertyTypes: activePropertyTypes,
          propertyLabel,
        },
      };

      // Priority 1: Explicit search
      if (searchText || cityIds.length) {
        payload.hostelSearchCriteria.searchText = searchText;
        payload.hostelSearchCriteria.cityIds = cityIds;
      }

      // Priority 2: City page
      else if (isCityPage && cityNameFromSlug) {
        payload.hostelSearchCriteria.searchText =
          cityNameFromSlug;
      }

      // Priority 3: Individual property slug
      else if (hostelSlug) {
        payload.hostelSearchCriteria.searchText =
          formatSlugToName(hostelSlug);
      }

      console.log("API PAYLOAD:", payload);

      const response = await axiosApi.post(
        "/public/getHostelData",
        payload
      );

      const data = response.data;

      if (Array.isArray(data)) {
        setHotels(data);
        setTotalPages(1);
      } else {
        setHotels(data?.content || []);
        setTotalPages(data?.totalPages || 0);
      }
    } catch (err) {
      console.error("Failed to fetch listings:", err);

      setError(
        err.message ||
          `Failed to load ${propertyLabel.toLowerCase()}.`
      );

      setHotels([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    size,
    location.search,
    hostelSlug,
    isCityPage,
    cityNameFromSlug,
    filters.sortOption,
    filters.preference,
    filters.gender,
    filters.occupancy,
    filters.priceRange,
    propertyTypes,
    propertyLabel,
    siteKey,
  ]);

  // --------------------------------------------------
  // FETCH ON ROUTE / FILTER CHANGE
  // --------------------------------------------------

  useEffect(() => {
    if (!showHostels) return;

    fetchHotels();
  }, [fetchHotels, showHostels]);

  // Reset pagination when search or filters change.
  useEffect(() => {
    setPage(0);
  }, [
    location.search,
    filters.sortOption,
    filters.preference,
    filters.gender,
    filters.occupancy,
    filters.priceRange,
  ]);

  // --------------------------------------------------
  // DEBUG ROUTE DATA
  // --------------------------------------------------

  useEffect(() => {
    console.log("---- DEBUG ROUTE DATA ----");
    console.log("pathname:", location.pathname);
    console.log("location.search:", location.search);
    console.log("hostelSlug:", hostelSlug);
    console.log("citySlug:", citySlug);
    console.log("isCityPage:", isCityPage);
    console.log("cityNameFromSlug:", cityNameFromSlug);
    console.log("siteKey:", siteKey);
    console.log("--------------------------");
  }, [
    location.pathname,
    location.search,
    hostelSlug,
    citySlug,
    cityNameFromSlug,
    isCityPage,
    siteKey,
  ]);

  // --------------------------------------------------
  // SEARCH NAVIGATION
  // --------------------------------------------------

  const handleSearch = ({
    cityIds,
    hostelNames,
    cityName,
  }) => {
    let searchText = "";

    if (Array.isArray(hostelNames)) {
      searchText = hostelNames[0] || "";
    } else if (typeof hostelNames === "string") {
      searchText = hostelNames;
    }

    searchText = searchText.trim();

    const params = new URLSearchParams();

    if (searchText) {
      params.set("searchText", searchText);
    }

    if (cityIds?.length) {
      params.set("cityIds", cityIds.join(","));
    }

    console.log("FINAL URL PARAMS:", params.toString());

    // City-only search
    if (cityIds?.length && !searchText && cityName) {
      const slug = cityName
        .toLowerCase()
        .replace(/\s+/g, "-");

      navigate(getSitePath(`/city/${slug}`));
      return;
    }

    // Other searches use active brand listing route.
    const query = params.toString();

    navigate(
      getSitePath(
        `${listingPath}${query ? `?${query}` : ""}`
      )
    );
  };

  // --------------------------------------------------
  // LOGIN / CONTACT
  // --------------------------------------------------

  const openLogin = () => setLoginOpen(true);
  const closeLogin = () => setLoginOpen(false);

  const openContact = () => setContactOpen(true);
  const closeContact = () => setContactOpen(false);

  // --------------------------------------------------
  // SEO
  // --------------------------------------------------

  let seoTitle = defaultTitle;
  let seoDescription = defaultDescription;

  const seoCanonical = `${canonicalBase}${location.pathname}`;

  if (isCityPage && cityNameFromSlug) {
    seoTitle = `${
      cityTitle || `${propertyLabel} in`
    } ${cityNameFromSlug} | ${brandName}`;

    seoDescription =
      `Discover ${propertyLabel.toLowerCase()} in ` +
      `${cityNameFromSlug}. Compare prices, amenities and ` +
      `locations on ${brandName}.`;
  } else if (
    hotels.length === 1 &&
    hostelSlug &&
    !isCityPage
  ) {
    const property = hotels[0];

    seoTitle =
      `${property.hostelName} | ${property.cityName} | ${brandName}`;

    seoDescription =
      `Explore ${property.hostelName} in ${property.cityName}. ` +
      `Discover amenities, accommodation and availability ` +
      `on ${brandName}.`;
  }

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>

        <meta
          name="description"
          content={seoDescription}
        />

        <meta
          name="keywords"
          content={
            `${propertyLabel} in ${cityNameFromSlug || ""}, ` +
            `accommodation in ${cityNameFromSlug || ""}, ` +
            `${brandName}`
          }
        />

        <meta name="robots" content="index, follow" />

        <meta property="og:title" content={seoTitle} />

        <meta
          property="og:description"
          content={seoDescription}
        />

        <meta
          property="og:image"
          content={getHostelImage(
            hotels[0]?.imageUploadConfigurationDTOs
          )}
        />

        <meta property="og:url" content={seoCanonical} />

        <link rel="canonical" href={seoCanonical} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name:
                  `Which ${propertyLabel.toLowerCase()} are ` +
                  `available in ${cityNameFromSlug || ""}?`,
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    `Explore ${propertyLabel.toLowerCase()} in ` +
                    `${cityNameFromSlug || ""}, compare amenities ` +
                    `and find suitable accommodation.`,
                },
              },
              {
                "@type": "Question",
                name:
                  `How can I compare accommodation in ` +
                  `${cityNameFromSlug || ""}?`,
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    `Compare accommodation options in ` +
                    `${cityNameFromSlug || ""} based on location, ` +
                    `amenities and property type.`,
                },
              },
            ],
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="hero-section">
        <div className="hero-topbar">
          <div className="hero-logo">
            <img src={logo} alt={brandName} />
            <span>{brandName}</span>
          </div>

          <div className="hero-actions">
            <Button
              className="cta-highlight"
              onClick={openContact}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textTransform: "none",
                borderRadius: "12px",
                px: 1.4,
                py: 0.45,
                minWidth: "unset",
                width: "fit-content",
                maxWidth: "180px",
                flexShrink: 0,
                border: "1px solid #fde68a",
                background:
                  "linear-gradient(135deg,#fff7cc,#fff)",
                color: "#92400e",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#fde68a,#fff)",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.05,
                  whiteSpace: "nowrap",
                  maxWidth: "160px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  List Your Property
                </span>

                <span
                  style={{
                    fontSize: "0.65rem",
                    opacity: 0.85,
                  }}
                >
                  Grow your business 🚀
                </span>
              </div>
            </Button>

            <Button
              variant="contained"
              onClick={openLogin}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 3,
                py: 0.7,
                fontSize: "0.9rem",
                minHeight: "38px",
                background:
                  "linear-gradient(135deg,#2563eb,#1d4ed8)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1d4ed8,#1e40af)",
                },
              }}
            >
              Login
            </Button>
          </div>
        </div>

        <div className="hero-bg-image"></div>
        <div className="hero-overlay"></div>

        <div className="hero-content hero-row">
          <div className="hero-text">
            <h1>
              {heroTitle || `Discover ${propertyLabel}`}
            </h1>

            <p className="hero-subtitle">
              {heroDescription ||
                `Discover ${propertyLabel.toLowerCase()} with ${brandName}.`}
            </p>
          </div>
        </div>
      </section>

      <Header
        onSearch={handleSearch}
        onApplyFilters={setFilters}
        externalLoginTrigger={openLogin}
      />

      {/* AI SEARCH APPLIED */}
      {isAISearch && (
        <div
          style={{
            maxWidth: "1200px",
            margin: "20px auto",
            background: "#EEF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: "14px",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 700,
                color: "#1D4ED8",
                marginBottom: "10px",
              }}
            >
              🤖 AI Search Applied
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {aiFilters.map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  style={{
                    background: "#fff",
                    border: "1px solid #D1D5DB",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(getSitePath(listingPath))}
          >
            Clear AI Filters
          </Button>
        </div>
      )}

      {/* LISTINGS */}
      <div className="container">
        <div className="content-area">
          {showHostels &&
            (loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={180}
                  className="skeleton-card"
                />
              ))
            ) : error ? (
              <p className="error-message">⚠️ {error}</p>
            ) : hotels.length ? (
              hotels.map((hotel) => (
                <HostelCard
                  key={hotel.id}
                  image={getHostelImage(
                    hotel.imageUploadConfigurationDTOs
                  )}
                  name={hotel.hostelName}
                  city={hotel.cityName}
                  hostelId={hotel.id}
                  totalBedsAvailableCount={
                    hotel.totalBedsAvailableCount
                  }
                  phone={
                    hotel.mobileNumber || hotel.phoneNumber
                  }
                  gender={hotel.hostelGenderType}
                  searchDates={searchDates}
                  propertyType={hotel.propertyType}
                />
              ))
            ) : (
              <div className="no-results-wrapper">
                <div className="no-results-card">
                  <h2>
                    {noResultsTitle ||
                      `No ${propertyLabel} Found`}
                  </h2>

                  <p>
                    {noResultsDescription ||
                      `We couldn't find matching ${propertyLabel.toLowerCase()}. Try adjusting your filters or searching another location.`}
                  </p>
                </div>
              </div>
            ))}

          {/* CITY SEO CONTENT */}
          {isCityPage && hotels.length > 0 && (
            <div className="seo-city-content">
              <h1>
                {cityTitle || `${propertyLabel} in`}{" "}
                {cityNameFromSlug}
              </h1>

              <p>
                Discover {propertyLabel.toLowerCase()} in{" "}
                {cityNameFromSlug}. Compare accommodation,
                amenities, locations and prices on {brandName}.
              </p>

              <h2>
                {propertyLabel} in {cityNameFromSlug}
              </h2>

              <p>
                Compare pricing, amenities, room types and
                locations in {cityNameFromSlug} before booking
                your stay.
              </p>

              <h2>
                Explore {propertyLabel} in {cityNameFromSlug}
              </h2>

              <ul>
                <li>Safe and secure accommodation</li>
                <li>Accommodation options for different needs</li>
                <li>Compare available amenities</li>
                <li>Explore convenient locations</li>
              </ul>

              <h2>Popular Areas in {cityNameFromSlug}</h2>

              <p>
                {propertyLabel} are available across areas in{" "}
                {cityNameFromSlug}, helping you compare stays
                by location.
              </p>
            </div>
          )}

          {/* SINGLE PROPERTY SEO CONTENT */}
          {hotels.length === 1 &&
            hostelSlug &&
            !isCityPage && (
              <div className="seo-hostel-content">
                <h1>
                  {hotels[0].hostelName} in{" "}
                  {hotels[0].cityName}
                </h1>

                <p>
                  Explore {hotels[0].hostelName} in{" "}
                  {hotels[0].cityName}. Discover property
                  details, amenities and accommodation options
                  on {brandName}.
                </p>
              </div>
            )}

          {/* FOOD SUPPLIERS */}
          {showFoodSuppliers &&
            foodSuppliers.map((supplier) => (
              <FoodSupplierCard
                key={supplier.name}
                {...supplier}
              />
            ))}

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "14px",
                marginTop: "32px",
                marginBottom: "30px",
                padding: "20px",
                background: "#ffffff",
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6b7280",
                }}
              >
                Page{" "}
                <span
                  style={{
                    color: "#2563eb",
                    fontWeight: 700,
                  }}
                >
                  {page + 1}
                </span>{" "}
                of{" "}
                <span
                  style={{
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {totalPages}
                </span>
              </div>

              <Pagination
                count={totalPages}
                page={page + 1}
                color="primary"
                shape="rounded"
                size="large"
                siblingCount={1}
                boundaryCount={1}
                onChange={(e, value) => {
                  setPage(value - 1);

                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                sx={{
                  "& .MuiPagination-ul": {
                    gap: "6px",
                  },
                  "& .MuiPaginationItem-root": {
                    borderRadius: "12px",
                    fontWeight: 600,
                    minWidth: "42px",
                    height: "42px",
                    border: "1px solid #e5e7eb",
                    transition: "all .25s ease",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: "#eff6ff",
                    transform: "translateY(-2px)",
                  },
                  "& .Mui-selected": {
                    background:
                      "linear-gradient(135deg,#2563eb,#1d4ed8) !important",
                    color: "#fff",
                    boxShadow:
                      "0 6px 14px rgba(37,99,235,.25)",
                  },
                  "& .MuiPaginationItem-previousNext": {
                    fontWeight: 700,
                  },
                }}
              />
            </div>
          )}
        </div>

        {/* SEO INTERNAL CITY LINKS */}
        {!isCityPage && (
          <div className="seo-city-links">
            <h2>Popular {propertyLabel} Cities</h2>

            <div className="city-links">
              {[
                "pala",
                "kottayam",
                "kochi",
                "thrissur",
                "alappuzha",
              ].map((city) => (
                <Link key={city} to={getCityPath(city)}>
                  {propertyLabel} in{" "}
                  {city.charAt(0).toUpperCase() +
                    city.slice(1)}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* LOGIN DIALOG */}
      <Dialog open={loginOpen} onClose={closeLogin}>
        <DialogTitle>Login</DialogTitle>

        <DialogContent>
          <LoginPage
            onClose={closeLogin}
            loginType="operator"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeLogin}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* CONTACT DIALOG */}
      <Contact
  open={contactOpen}
  onClose={closeContact}
  propertyTypes={SITE_CONFIG.propertyTypes}
/>

     <AIChatWidget propertyTypes={SITE_CONFIG.propertyTypes} />
    </>
  );
};

export default Sidebar;